import { Play, ArrowRight, MessageCircle, Rocket } from "lucide-react";
import { LocaleLink } from "../i18n/LocaleLink";
import { useLocale } from "../i18n/useLocale";
import { cmsImage } from "../i18n/cmsImage";
import { Container } from "../components/ui";
import { MotionProvider, Reveal, PromoBanner, Button as DSButton, PageHeading, SectionHeading } from "../ds/core";
import { TRIAL_PATH as TRIAL } from "../config/entrypoints";

const U = (id: string) => `https://images.unsplash.com/${id}?w=1800&auto=format&fit=crop&q=80`;
const FEATURED_POSTER = U("photo-1521737604893-d14cc237f11d");

const DOTS = { backgroundImage: "radial-gradient(rgba(255,255,255,0.12) 1px, transparent 1px)", backgroundSize: "16px 16px" } as const;
const SCRIM = "linear-gradient(180deg, rgba(8,11,22,0.3) 0%, rgba(8,11,22,0.25) 40%, rgba(8,11,22,0.78) 100%)";

/* Real training videos from the current site (done-it.app/videos). Titles only — the
 * live page lists no durations. Grouped for scanning; add a video by appending an entry.
 * `id` is the i18n key suffix; `group` joins a video to its GROUPS entry. */
const VIDEOS = [
  { id: "mobileApp", group: "gettingStarted" },
  { id: "companyDetails", group: "gettingStarted" },
  { id: "addProject", group: "gettingStarted" },
  { id: "addContact", group: "gettingStarted" },
  { id: "timeRegistrations", group: "dailyUse" },
  { id: "holidaysAbsences", group: "dailyUse" },
  { id: "extraWork", group: "dailyUse" },
  { id: "overtime", group: "dailyUse" },
  { id: "reports", group: "forTheOffice" },
] as const;

const GROUPS = [
  { key: "gettingStarted" },
  { key: "dailyUse" },
  { key: "forTheOffice" },
] as const;

function VideoCard({ title, desc }: { title: string; desc: string }) {
  return (
    <div className="group flex h-full w-full cursor-pointer flex-col overflow-hidden rounded-3xl border-8 border-white bg-bg-subtle shadow-[0_1px_3px_rgba(15,23,42,0.05)] transition-shadow duration-300 hover:shadow-[0_20px_50px_-26px_rgba(15,23,42,0.35)]">
      <div className="relative aspect-video shrink-0 overflow-hidden rounded-2xl bg-ds-hero">
        <div className="absolute inset-0 opacity-60" style={DOTS} />
        <span className="absolute left-1/2 top-1/2 grid h-16 w-16 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full bg-white/15 ring-1 ring-white/50 backdrop-blur-md transition-transform duration-300 group-hover:scale-110">
          <Play size={24} className="ml-0.5 text-white" fill="currentColor" />
        </span>
      </div>
      <div className="flex flex-1 flex-col px-3 pb-5 pt-4">
        <h3 className="font-display text-ds-18 font-semibold leading-snug text-ds-ink">{title}</h3>
        <p className="mt-1.5 text-ds-14 leading-relaxed text-ds-ink-soft">{desc}</p>
      </div>
    </div>
  );
}

export default function Trainings() {
  const { t } = useLocale();
  return (
    <>
      {/* 1 ── Blue hero */}
      <PageHeading
        eyebrow={t("trainings:hero.eyebrow")}
        title={t("trainings:hero.title")}
        intro={t("trainings:hero.intro")}
        actions={<>
          <DSButton to={TRIAL} variant="solid-light">{t("trainings:hero.startTrial")}</DSButton>
          <DSButton to="/getting-started" variant="ghost">{t("trainings:hero.gettingStarted")}</DSButton>
        </>}
      />

      {/* 2 ── Featured video — poster, scrim, glass play button */}
      <section className="bg-bg font-ds">
        <div className="px-4 py-3">
          <div className="group relative block aspect-[16/10] cursor-pointer overflow-hidden rounded-[32px] bg-ds-hero md:aspect-[16/7]">
            <img src={cmsImage(t, "trainings:featured.image", FEATURED_POSTER)} alt={t("trainings:featured.posterAlt")} loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1200ms] group-hover:scale-105" />
            <div aria-hidden className="absolute inset-0" style={{ background: SCRIM }} />

            <span className="absolute left-1/2 top-1/2 grid h-24 w-24 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full bg-white/15 ring-1 ring-white/50 backdrop-blur-md transition-transform duration-300 group-hover:scale-110">
              <span aria-hidden className="absolute inset-0 animate-ping rounded-full ring-1 ring-white/40" />
              <Play size={30} className="relative ml-1 text-white" fill="currentColor" />
            </span>

            <div className="absolute inset-x-0 bottom-0 p-8 md:p-12">
              <SectionHeading tone="dark" eyebrow={t("trainings:featured.eyebrow")} title={t("trainings:video.mobileApp.title")} />
            </div>
          </div>
        </div>
      </section>

      {/* 3 ── Video library — grouped, DS cards */}
      <MotionProvider>
        <section className="bg-bg font-ds">
          <Container className="py-16 md:py-20">
            <div className="flex flex-col gap-14">
              {GROUPS.map((g) => (
                <div key={g.key}>
                  <SectionHeading eyebrow={t(`trainings:group.${g.key}.eyebrow`)} title={t(`trainings:group.${g.key}.title`)} />
                  <div className="mt-8 grid gap-4 sm:grid-cols-2">
                    {VIDEOS.filter((v) => v.group === g.key).map((v, i) => (
                      <Reveal key={v.id} delay={i * 0.05} className="flex">
                        <VideoCard title={t(`trainings:video.${v.id}.title`)} desc={t(`trainings:video.${v.id}.desc`)} />
                      </Reveal>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </Container>
        </section>
      </MotionProvider>

      {/* 4 ── Cross-links */}
      <section className="bg-bg-subtle font-ds">
        <Container className="py-16">
          <div className="grid gap-3 md:grid-cols-2">
            <LocaleLink to="/getting-started" className="group flex flex-col rounded-3xl border border-line bg-surface p-6 md:p-8 transition-colors hover:bg-white">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-ds-tint text-ds-accent transition-transform duration-300 ease-out group-hover:-rotate-6 group-hover:scale-110">
                <Rocket size={20} strokeWidth={2} />
              </span>
              <h3 className="mt-4 font-display text-ds-22 font-semibold text-ds-ink">{t("trainings:crossLinks.gettingStarted.title")}</h3>
              <p className="mt-2 flex-1 text-ds-14 leading-relaxed text-ds-ink-soft">{t("trainings:crossLinks.gettingStarted.body")}</p>
              <span className="mt-4 inline-flex items-center gap-1 text-[13px] font-semibold text-primary">
                {t("trainings:crossLinks.gettingStarted.cta")} <ArrowRight size={14} strokeWidth={2} className="transition-transform group-hover:translate-x-0.5" />
              </span>
            </LocaleLink>
            <LocaleLink to="/contact" className="group flex flex-col rounded-3xl border border-line bg-surface p-6 md:p-8 transition-colors hover:bg-white">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-ds-tint text-ds-accent transition-transform duration-300 ease-out group-hover:-rotate-6 group-hover:scale-110">
                <MessageCircle size={20} strokeWidth={2} />
              </span>
              <h3 className="mt-4 font-display text-ds-22 font-semibold text-ds-ink">{t("trainings:crossLinks.contact.title")}</h3>
              <p className="mt-2 flex-1 text-ds-14 leading-relaxed text-ds-ink-soft">{t("trainings:crossLinks.contact.body")}</p>
              <span className="mt-4 inline-flex items-center gap-1 text-[13px] font-semibold text-primary">
                {t("trainings:crossLinks.contact.cta")} <ArrowRight size={14} strokeWidth={2} className="transition-transform group-hover:translate-x-0.5" />
              </span>
            </LocaleLink>
          </div>
        </Container>
      </section>

      {/* 5 ── Closing CTA */}
      <PromoBanner
        eyebrow={t("trainings:promo.eyebrow")}
        title={t("trainings:promo.title")}
        body={t("trainings:promo.body")}
        action={<DSButton to={TRIAL} variant="solid-light">{t("trainings:promo.startTrial")}</DSButton>}
      />
    </>
  );
}

